import { useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Download, FileUp, Loader2, Paperclip, Trash2 } from 'lucide-react';

import { deleteFile, getDownloadUrl, listFilesForEntity, type FileAttachment } from '@/api/files.api';
import { Button } from '@/components/ui/Button';
import { useFileUpload } from '@/hooks/useFileUpload';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/cn';

/**
 * Reusable attachments panel — drop / pick to upload, list, download, and
 * remove files bound to a host record (`entityType` + `entityId`). Embed
 * it under a todo, rock, meeting, issue, SOP, etc.
 *
 * Uploads go straight to the bucket via `useFileUpload` (presign → PUT →
 * confirm). When storage is unconfigured the panel switches to a friendly
 * disabled state instead of erroring — the server enforces size + type;
 * the hints here are cosmetic.
 */

export interface FileAttachmentsProps {
  entityType: string;
  entityId: string;
  /** View-only: hide the uploader + per-file remove controls. */
  readOnly?: boolean;
  /** Native picker filter, e.g. `image/*` or `.pdf`. Cosmetic only. */
  accept?: string;
  /** Size hint shown in the dropzone copy; the server enforces the real cap. */
  maxSizeHintMb?: number;
  className?: string;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
}

export function FileAttachments({
  entityType,
  entityId,
  readOnly = false,
  accept,
  maxSizeHintMb,
  className,
}: FileAttachmentsProps) {
  const { show } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setDragging] = useState(false);

  const queryKey = ['files', entityType, entityId] as const;

  const filesQuery = useQuery({
    queryKey,
    queryFn: () => listFilesForEntity(entityType, entityId),
  });

  const { state, upload, reset } = useFileUpload({ entityType, entityId });

  const deleteMutation = useMutation({
    mutationFn: (fileId: string) => deleteFile(fileId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
    onError: () => {
      show('Could not remove the file.', { variant: 'danger' });
    },
  });

  async function handleFiles(fileList: FileList | null): Promise<void> {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    // Sequential so the single hook state cleanly reflects one active
    // upload at a time; each success refreshes the list. Stop the batch on
    // the first failure (bad type, over-size, or storage disabled) — the
    // hook surfaces the reason.
    for (const file of files) {
      const result = await upload(file);
      if (!result) break;
      await queryClient.invalidateQueries({ queryKey });
    }
  }

  async function handleDownload(file: FileAttachment): Promise<void> {
    try {
      const { url } = await getDownloadUrl(file.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      show('Could not open that file.', { variant: 'danger' });
    }
  }

  function onDrop(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    setDragging(false);
    if (readOnly) return;
    void handleFiles(e.dataTransfer.files);
  }

  function onPick(e: ChangeEvent<HTMLInputElement>): void {
    void handleFiles(e.target.files);
    e.target.value = ''; // allow re-picking the same file
  }

  const files = filesQuery.data ?? [];
  const busy = state.phase === 'presigning' || state.phase === 'uploading' || state.phase === 'confirming';
  const percent = Math.round(state.progress * 100);

  return (
    <div className={cn('space-y-3', className)}>
      {state.storageDisabled ? (
        <div className="flex items-start gap-2 rounded-kw-md border border-kw-border bg-kw-bg p-3">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-muted" />
          <p className="text-[12px] text-kw-muted">
            File storage isn’t configured in this environment, so uploads are disabled. Existing attachments
            still list below.
          </p>
        </div>
      ) : readOnly ? null : (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => {
            setDragging(false);
          }}
          onDrop={onDrop}
          className={cn(
            'rounded-kw-md border border-dashed p-4 text-center transition-colors',
            isDragging ? 'border-kw-cerulean bg-kw-cerulean-light' : 'border-kw-border bg-kw-bg',
          )}
        >
          <div className="flex flex-col items-center gap-2">
            <Paperclip className="size-5 text-kw-muted" />
            <p className="text-[13px] text-kw-shark">
              Drag a file here, or{' '}
              <button
                type="button"
                className="font-medium text-kw-cerulean underline-offset-2 hover:underline"
                onClick={() => fileInputRef.current?.click()}
              >
                browse
              </button>
              .
            </p>
            <p className="text-[11px] text-kw-muted">
              Images, PDF, Word, audio, zip, CSV/text
              {maxSizeHintMb ? ` · up to ${String(maxSizeHintMb)} MB` : ''}
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={accept}
            className="hidden"
            aria-label="Attach file"
            onChange={onPick}
          />
        </div>
      )}

      {busy ? (
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-[12px] text-kw-muted">
            <Loader2 className="size-3.5 animate-spin" />
            {state.phase === 'uploading' ? `Uploading… ${String(percent)}%` : 'Finishing up…'}
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-kw-border">
            <div
              className="h-full bg-kw-cerulean transition-[width]"
              style={{ width: `${String(state.phase === 'uploading' ? percent : 100)}%` }}
            />
          </div>
        </div>
      ) : null}

      {state.phase === 'error' && state.error ? (
        <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-2">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
          <p className="flex-1 text-[12px] text-kw-danger-text">{state.error}</p>
          <button
            type="button"
            className="text-[12px] font-medium text-kw-danger-text underline-offset-2 hover:underline"
            onClick={reset}
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {files.length > 0 ? (
        <ul className="divide-y divide-kw-border overflow-hidden rounded-kw-md border border-kw-border">
          {files.map((file) => (
            <li key={file.id} className="flex items-center gap-3 bg-white px-3 py-2">
              <button
                type="button"
                onClick={() => void handleDownload(file)}
                className="flex min-w-0 flex-1 items-center gap-2 text-left"
                title={`Download ${file.filename}`}
              >
                <Download className="size-4 shrink-0 text-kw-cerulean" />
                <span className="truncate text-[13px] text-kw-shark">{file.filename}</span>
                <span className="shrink-0 text-[11px] text-kw-muted">{formatBytes(file.sizeBytes)}</span>
              </button>
              {readOnly ? null : (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-kw-muted hover:text-kw-danger-text"
                  aria-label={`Remove ${file.filename}`}
                  isLoading={deleteMutation.isPending && deleteMutation.variables === file.id}
                  onClick={() => {
                    deleteMutation.mutate(file.id);
                  }}
                >
                  <Trash2 className="size-4" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      ) : filesQuery.isLoading ? (
        <p className="text-[12px] text-kw-muted">Loading attachments…</p>
      ) : (
        <p className="text-[12px] text-kw-muted">
          <FileUp className="mr-1 inline size-3.5 align-text-bottom" />
          No attachments yet.
        </p>
      )}
    </div>
  );
}
